/* eslint-disable react/forbid-component-props */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSpring } from 'react-spring';
import { useDispatch, useSelector } from 'react-redux';
import { SelectField } from '@oneaudi/unified-web-alpha-components';
import useAnimations from '../../../hooks/useAnimations';
import useClickOutside from '../../../hooks/useClickOutside';
import {
  DropDownWrapper,
  DropDownInput,
  DropDownSelect,
  ListOfOptions,
  TopicOption,
  TitlePlaceholder,
  ArrowIcons,
  Checkbox,
  HiddenInput,
  DropDown,
  DropdownLabel,
  SelectedPlaceholder,
} from './vehicleDropDown.styles';
import ArrowUpIcon from '../../../assets/icons/ArrowUpIcon';
import ArrowDownIcon from '../../../assets/icons/ArrowDownIcon';
import CheckMarkIcon from '../../../assets/icons/CheckMarkIcon';
import { COLORS } from '../../../styles/variables';
import { setUserInputs, selectInputs } from '../../../redux/inputsReducer';
import { OptionalNote } from '../CheckboxInput/CheckBoxInput.styles';
import { INPUT_TYPE, FEATURE_APP_DETAILS } from '../../../constants';
import { LIST_OF_VEHICLES } from '../../../constants/vehicles';
import {
  digitalLeadsFormInput,
  setModelLineSelectCustom,
} from '../../../utils/analyticsEventListeners';
import { selectDealerAndVehicleData } from '../../../redux/dealerAndVehicleReducer';
import { selectForm } from '../../../redux/formReducer';
import { selectAppConfig } from '../../../redux/appConfigReducer';
import { convertToPromotionsType } from '../../../functions/convertToPromotionsType';
import { getVehicleForPromotions } from '../../../functions/getVehicleForPromotions';
import type { Form } from '../../../types/analytics';

interface VehicleDropdownProps {
  options: string[];
  inputLabel: string;
  inputKey: string;
  isOptional?: boolean;
  dropdownHeight?: number;
}

const VehicleDropdown: React.FC<VehicleDropdownProps> = ({
  options,
  inputLabel,
  inputKey,
  isOptional,
  dropdownHeight = 250,
}) => {
  const dispatch = useDispatch();
  const inputs = useSelector(selectInputs);
  const inputValue = inputs[inputKey];
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [focused, setFocused] = useState(false);

  const { vehicle, vehicleByCarline, vehicleByAudiCode } = useSelector(selectDealerAndVehicleData);
  const contactForm = useSelector(selectForm);
  const { offers } = useSelector(selectAppConfig);
  const promotion = convertToPromotionsType(offers!);

  const isAnySelected = !!inputValue;

  const { dropdownAnimation, filtersAccordianAnimation } = useAnimations(
    isDropdownOpen,
    dropdownHeight,
  );

  useEffect(() => {
    const { modelFamilyOfInterest } = inputs;
    if (
      inputKey === INPUT_TYPE.MODEl_LINE_OF_INTEREST &&
      modelFamilyOfInterest &&
      !inputValue &&
      options.length > 0
    ) {
      dispatch(
        setUserInputs({
          inputName: inputKey,
          inputValue: options[0],
        }),
      );
    }
  }, [inputKey, inputValue, options, dispatch, inputs]);

  useEffect(() => {
    if (inputKey === INPUT_TYPE.MODEl_LINE_SELECT_CUSTOM && !inputValue && options.length > 0) {
      dispatch(
        setUserInputs({
          inputName: inputKey,
          inputValue: options[0],
        }),
      );
    }
  }, [inputKey, inputValue, options, dispatch]);

  const inputAnimation = useSpring({
    to: {
      top: focused || inputValue ? -18 : 8,
      fontSize: focused || inputValue ? '12px' : '16px',
    },
  });

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  const onHandleChange = useCallback(
    (value: string) => {
      const inputTouched = {
        inputTitle: inputKey,
        inputValue: value,
        action: 'select',
        inputType: 'dropdown',
      };

      const vehicleInView = vehicle || vehicleByCarline || vehicleByAudiCode;
      const vehicleData = vehicleInView || (offers ? getVehicleForPromotions(offers) : undefined);

      const updatedForm: Form = {
        ...contactForm,
        ...{ formData: { ...contactForm.formData, ...{ [inputKey]: value } } },
      };

      dispatch(setUserInputs({ inputName: inputKey, inputValue: value }));

      // Update model line context before firing analytics so the event
      // carries the newly selected value instead of the previous one.
      if (inputKey === INPUT_TYPE.MODEl_LINE_SELECT_CUSTOM) {
        setModelLineSelectCustom(value || undefined);
      }

      digitalLeadsFormInput(updatedForm, vehicleData, FEATURE_APP_DETAILS, promotion, inputTouched);

      setIsDropdownOpen(false);
    },
    [
      inputKey,
      dispatch,
      vehicle,
      vehicleByCarline,
      vehicleByAudiCode,
      offers,
      contactForm,
      promotion,
    ],
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent, value: string) => {
      if (e.key !== ' ') return;
      e.preventDefault();
      onHandleChange(value);
    },
    [onHandleChange],
  );

  const dropdownRef = useRef(null);

  useClickOutside(
    dropdownRef,
    useCallback(() => {
      setIsDropdownOpen(false);
    }, [setIsDropdownOpen]),
  );

  if (!process.env.IS_LEGACY) {
    return (
      <DropDownWrapper ref={dropdownRef} isOptional={isOptional} data-testid="dropdown-wrapper">
        <SelectField
          appendix={isOptional ? 'optional' : ''}
          label={`${inputLabel} ${!isOptional ? '*' : ''}`}
          data-testid={`dropdown-selector-${inputKey}`}
          id="dropdown-selector"
          onChange={(e) => {
            onHandleChange(e.target.value);
          }}
          value={inputValue || ''}
        >
          {options.map((option) => {
            const label =
              inputKey === INPUT_TYPE.MODEl_FAMILY_OF_INTEREST
                ? LIST_OF_VEHICLES[option as keyof typeof LIST_OF_VEHICLES]?.label
                : option;
            return (
              <option key={option} value={option}>
                {label}
              </option>
            );
          })}
        </SelectField>
      </DropDownWrapper>
    );
  }

  return (
    <DropDownWrapper ref={dropdownRef} isOptional={isOptional} data-testid="dropdown-wrapper">
      <DropDownInput
        onClick={() => {
          setIsDropdownOpen(!isDropdownOpen);
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        data-testid="vehicle-dropdown-trigger"
      >
        <TitlePlaceholder style={inputAnimation}>
          {inputLabel}
          {isOptional && <OptionalNote>(optional)</OptionalNote>}
        </TitlePlaceholder>
        {isAnySelected && (
          <SelectedPlaceholder>
            {inputKey === INPUT_TYPE.MODEl_FAMILY_OF_INTEREST
              ? LIST_OF_VEHICLES[inputValue as keyof typeof LIST_OF_VEHICLES]?.label
              : inputValue}
          </SelectedPlaceholder>
        )}

        <ArrowIcons>
          {isDropdownOpen ? (
            <ArrowUpIcon color={COLORS.grey60} />
          ) : (
            <ArrowDownIcon color={COLORS.grey60} />
          )}
        </ArrowIcons>
      </DropDownInput>
      {dropdownAnimation(
        (props, item) =>
          item && (
            <DropDownSelect style={props}>
              <DropDown style={filtersAccordianAnimation} data-testid="dropdown-selector">
                <ListOfOptions>
                  {options?.map((option: any) => {
                    const label =
                      inputKey === INPUT_TYPE.MODEl_FAMILY_OF_INTEREST
                        ? LIST_OF_VEHICLES[option as keyof typeof LIST_OF_VEHICLES]?.label
                        : option;
                    const value =
                      inputKey === INPUT_TYPE.MODEl_FAMILY_OF_INTEREST
                        ? LIST_OF_VEHICLES[option as keyof typeof LIST_OF_VEHICLES]?.key
                        : option;
                    const checked = inputValue === value;
                    return (
                      <TopicOption
                        key={value}
                        onClick={() => {
                          onHandleChange(value);
                        }}
                      >
                        <Checkbox
                          tabIndex={0}
                          onClick={() => {
                            onHandleChange(value);
                          }}
                          role="checkbox"
                          aria-checked={checked}
                          onKeyDown={(e) => {
                            handleKeyPress(e, value);
                          }}
                          checked={checked}
                          data-testid="dropdown-option-checkbox"
                        >
                          {checked && <CheckMarkIcon />}
                        </Checkbox>
                        <HiddenInput
                          type="checkbox"
                          onChange={() => {
                            onHandleChange(value);
                          }}
                          checked={checked}
                          name={value}
                        />
                        <DropdownLabel>{label}</DropdownLabel>
                      </TopicOption>
                    );
                  })}
                </ListOfOptions>
              </DropDown>
            </DropDownSelect>
          ),
      )}
    </DropDownWrapper>
  );
};

export default VehicleDropdown;
